import { useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { GridRowModes, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { v4 as uuidv4 } from 'uuid';
import { Box, Typography, Paper } from '@mui/material';
import dayjs from 'dayjs';

import { dataGridPremiumColumns } from '../../../utils/helper';
import { DataGridIdentifiers, FormErrorUI, DatagridPremiumUi, InputUI, DialogUI, ButtonUI } from '../../Interface';

import documentService from '../../../service/newDoc.service';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';

const ProductionBatchOutDialog = ({
  open,
  onClose,
  disabled = false,
  //selectedLineIndex,
  //docLine,
  selectedLine,
  whsCode,
  defaultBatchLine,
  onSave,
}) => {
  const apiRef = useGridApiRef();
  const [batches, setBatches] = useState([]);
  const [batchOut, setBatchOut] = useState([]);
  const { selectedLineIndex } = selectedLine || {};

  const getBatches = () => {
    documentService
      .getBatches({
        warehouseCode: whsCode,
        itemCode: selectedLine?.itemCode,
      })
      .then((data) => {
        setBatches(data);
      });
  };

  useEffect(() => {
    if (!whsCode) {
      addNotification({
        content: 'Nincs megadott raktár!',
        type: NotificationType.WARNING,
      });
    }
    if (!selectedLine?.itemCode) return;
    getBatches();
    setBatchOut(selectedLine?.docLineBatchSn ? [...selectedLine.docLineBatchSn] : []);
  }, [whsCode, selectedLine?.itemCode, open]);

  if (typeof selectedLineIndex !== 'number') return null;

  const handleAddClick = (row) => {
    const newBatchLine = {
      ...defaultBatchLine,
      id: uuidv4(),
      batchSn: row.batchSn,
    };
    setBatchOut((prev) => [...prev, newBatchLine]);
    setTimeout(() => {
      apiRef.current.startRowEditMode({ id: newBatchLine.id });
    }, 0);
  };

  const columns = (push) => {
    return dataGridPremiumColumns([
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id, row }) => {
          return [
            <GridActionsCellItem
              icon={<AddIcon />}
              label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleAddClick(row, push);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
      {
        field: 'batchSn',
        headerName: 'Sarzs',
        type: 'string',
        sortable: false,
      },
      {
        field: 'quantity',
        headerName: 'Mennyiség',
        type: 'number',
        sortable: false,
      },
      {
        field: 'lot',
        headerName: 'LOT',
        type: 'string',
        sortable: false,
      },
      {
        field: 'supplierId',
        headerName: 'Beszállító azon.',
        type: 'string',
        sortable: false,
      },
      {
        field: 'expDate',
        headerName: 'Lejárat',
        type: 'date',

        sortable: false,
        valueGetter: (_data, row) => {
          return row.expDate ? new Date(row.expDate) : null;
        },
        renderCell: (data) => {
          return data.row.expDate ? dayjs(data.row.expDate).format('YYYY.MM.DD') : '';
        },
      },
      {
        field: 'mnfDate',
        headerName: 'Gyártás',
        type: 'date',
        sortable: false,
        valueGetter: (_data, row) => {
          return row.mnfDate ? new Date(row.mnfDate) : null;
        },
        renderCell: (data) => {
          return data.row.mnfDate ? dayjs(data.row.mnfDate).format('YYYY.MM.DD') : '';
        },
      },
      {
        field: 'batchAttribute1',
        headerName: 'Attribútum 1',
        type: 'string',
        sortable: false,
      },
      {
        field: 'batchAttribute2',
        headerName: 'Attribútum 2',
        type: 'string',
        sortable: false,
      },
      {
        field: 'comment',
        headerName: 'Megjegyzés',
        type: 'string',
        sortable: false,
      },
      {
        field: 'price',
        headerName: 'Ár',
        type: 'string',
        sortable: false,
        valueGetter: (data, row) => {
          return row.price ?? '';
        },
      },
      {
        field: 'other',
        headerName: 'Egyéb',
        type: 'string',
        sortable: false,
      },
    ]);
  };

  const batchOutCols = () => {
    return dataGridPremiumColumns([
      {
        field: 'batchSn',
        headerName: 'Sarzs',
        type: 'string',
      },
      {
        field: 'quantity',
        headerName: 'Mennyiség',
        type: 'number',
        editable: true,
        renderEditCell: (data) => {
          const batchIndex = batchOut.findIndex((b) => b.id === data.id);
          return (
            <InputUI
              type="number"
              value={batchOut[batchIndex]?.quantity ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                setBatchOut((prev) => prev.map((b, i) => (i === batchIndex ? { ...b, quantity: value } : b)));
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: value,
                });
              }}
              fullWidth
              size="small"
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id, row }) => [
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setBatchOut((prev) => prev.filter((b) => b.id !== id));
            }}
            color="inherit"
            disabled={disabled}
          />,
        ],
      },
    ]);
  };

  return (
    <DialogUI open={open} onClose={onClose} headerContent={'Sarzs'} maxWidth="lg" fullWidth>
      <Box sx={{ overflowX: 'auto' }}>
        <Box className="align-middle">
          <Paper elevation={1} sx={{ overflow: 'hidden', borderRadius: 2 }}>
            <Box display="flex" gap={2} p={2} alignItems="center" bgcolor="#f5f5f5" mb={2}>
              <Box flex={0.35} minWidth={0} display="flex" alignItems="center">
                <Typography fontWeight="bold">Sor: </Typography>
                <Typography>{selectedLineIndex + 1}</Typography>
              </Box>
              <Box flex={1} minWidth={0} display="flex" alignItems="center">
                <Typography fontWeight="bold">Cikkszám: </Typography>
                <Typography>{selectedLine?.itemCode ?? ''}</Typography>
              </Box>
              <Box flex={1} minWidth={0} display="flex" alignItems="center">
                <Typography fontWeight="bold">Nyitott mennyiség: </Typography>
                <Typography>{selectedLine?.qty ?? ''}</Typography>
              </Box>
              <Box flex={1} minWidth={0} display="flex" alignItems="center">
                <Typography fontWeight="bold">Összesített sarzs: </Typography>
                <Typography>
                  {(batchOut ?? []).reduce((sum, batch) => sum + (Number(batch.quantity) || 0), 0)}
                </Typography>
              </Box>
            </Box>

            <Box display="flex" gap={2}>
              <Box flex={1} minWidth={0}>
                <DatagridPremiumUi
                  datagridIdentifier={DataGridIdentifiers.BatchInStockTable}
                  height={480}
                  columns={columns()}
                  rows={batches ?? []}
                  slots={{
                    toolbar: null,
                  }}
                  onCellDoubleClick={(_params, event) => {
                    event.defaultMuiPrevented = true;
                  }}
                  headerFilters={false}
                  getRowHeight={() => 'auto'}
                  getRowId={(row) => row.batchSnId}
                />
              </Box>
              <Box flex={1} minWidth={0}>
                <DatagridPremiumUi
                  apiRef={apiRef}
                  datagridIdentifier={DataGridIdentifiers.BatchOutTable}
                  height={480}
                  columns={batchOutCols()}
                  rows={batchOut}
                  editMode="row"
                  onCellDoubleClick={(_params, event) => {
                    event.defaultMuiPrevented = true;
                  }}
                  slots={{
                    toolbar: null,
                  }}
                  headerFilters={false}
                  getRowHeight={() => 'auto'}
                  onCellClick={(params, event) => {
                    if (disabled) return;
                    const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

                    for (const rowId of rowsInEditMode) {
                      if (params.row.id.toString() !== rowId) {
                        apiRef.current.stopRowEditMode({ id: rowId });
                      }
                    }

                    const rowMode = apiRef.current.getRowMode(params.row.id);
                    if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <ButtonUI
                text="Mentés"
                className="bg-success"
                onClick={() => {
                  if (onSave) onSave(batchOut);
                  onClose();
                }}
              />
              <ButtonUI text="Mégse" color="blue-gray" variant="gradient" onClick={onClose} />
            </Box>
          </Paper>
        </Box>
      </Box>
    </DialogUI>
  );
};

export default ProductionBatchOutDialog;
